import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, CircularProgress, Button, Card, CardContent, CardMedia, AlertColor } from '@mui/material';
import JsBarcode from 'jsbarcode';
import { Meal, mealService, Order } from '../services';

import { format, addDays, isAfter, set } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

interface SnackbarState {
    open: boolean;
    message: string;
    severity: AlertColor;
}

const Home: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
    const [currentMeal, setCurrentMeal] = useState<Meal | null>(null);
    const [loading, setLoading] = useState(true);
    const [isDelivered, setIsDelivered] = useState(false);
    const [displayDate, setDisplayDate] = useState<Date>(new Date());
    const barcodeRef = useRef<SVGSVGElement>(null);
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success'
    });

    const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const orders = await mealService.getOrders();
                const now = new Date();
                const cutoffTime = set(now, { hours: 17, minutes: 0, seconds: 0 });
                const isAfterCutoff = isAfter(now, cutoffTime);
                const targetDate = isAfterCutoff ? addDays(now, 1) : now;
                setDisplayDate(targetDate);

                const targetDateString = format(targetDate, 'EEEE dd. MMMM')
                const order = orders?.find(o => o?.deliveryDate?.startsWith(format(targetDate,'yyyy-MM-dd')));
                setCurrentOrder(order || null);

                if (order) {
                    const meal = await mealService.getMealById(order.mealId);
                    setCurrentMeal(meal || null);
                }
            } catch (error) {
                console.error('Failed to fetch order:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, []);

    useEffect(() => {
        if (currentOrder && !isDelivered && barcodeRef.current) {
            JsBarcode(barcodeRef.current, `order-${currentOrder.id}`, {
                format: "CODE128",
                width: 2,
                height: 100,
                displayValue: true
            });
        }
    }, [currentOrder, isDelivered]);

    const handleDeliveryConfirmation = async () => {
        if (!currentOrder) return;

        try {
            setLoading(true);
            const success = await mealService.confirmDelivery(currentOrder.id);

            if (success) {
                setIsDelivered(true);
                setSnackbar({
                    open: true,
                    message: t('deliveryConfirmationSuccess'),
                    severity: 'success'
                });
            } else {
                throw new Error('Failed to confirm delivery');
            }
        } catch (error) {
            console.error('Failed to confirm delivery:', error);
            setSnackbar({
                open: true,
                message: t('deliveryConfirmationError'),
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const getColorForMeal = (mealId: number): string => {
        const colors = ['#FFB3BA', '#BAFFC9', '#BAE1FF', '#FFFFBA', '#FFD8BA'];
        return colors[mealId % colors.length];
    };

    if (loading) {
        return <CircularProgress />;
    }

    return (
        <Box sx={{ maxWidth: 600, margin: 'auto', mt: 4 }}>
            {currentOrder && !isDelivered && (
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            {dateFormatter.format(new Date(displayDate || ''))} {t('meal')}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            {format(displayDate, 'MMMM d, yyyy')}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {t('scanBarcode')}
                        </Typography>
                        <QRCodeSVG
                            value='http://localhost.com/?orderid='
                            title={t('scanBarcode')}
                            size={128}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleDeliveryConfirmation}
                            sx={{ mt: 2 }}
                        >
                            {t('confirmDelivery')}
                        </Button>
                    </Box>
                </Paper>
            )}


            {currentOrder && currentMeal ? (
                <Card sx={{ mb: 3, borderLeft: 6, borderColor: getColorForMeal(currentOrder.mealId) }}>
                    <CardMedia
                        component="img"
                        height="140"
                        image={currentMeal.imagePath}
                        alt={currentMeal.name}
                    />
                    <CardContent>
                        <Typography variant="h5" gutterBottom>
                            {currentMeal.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {currentMeal.description}
                        </Typography>
                        <Typography variant="body2">
                            Status: {isDelivered ? t('Delivered') : t('NotDelivered')}
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="body1">
                        {t('noMealOrdered')} {t(format(displayDate, "EEEE"))} {format(displayDate, "d")}. {t(format(displayDate, "MMMM"))}
                    </Typography>
                </Paper>
            )}

        </Box>
    );
};

export default Home;